/**
 * MessageAttachmentButtonPure
 *
 * A file-attach affordance for the message composer. Two presentations:
 *   - `variant="button"` (default): a compact paperclip button that opens the file
 *     picker, and — once a file is chosen — a chip showing the filename with a remove
 *     control. Used inline among other composer action buttons.
 *   - `variant="row"`: a full-width clickable field styled like a `FilterSelect`
 *     trigger (paperclip icon + placeholder), meant to sit directly under a message
 *     composer, mirroring the "append a voice clip" row. Clicking anywhere on the row
 *     opens the picker.
 *
 * Pure/presentational: the parent owns upload + attachment state and passes the
 * current filename plus select/remove callbacks.
 *
 * LinkedIn (via Unipile) caps a message's attachments at 20MB combined. We support a
 * single file for now, so `maxSizeBytes` doubles as the per-file limit and is enforced
 * here before the parent ever uploads.
 */
import { useRef } from 'react';
import type { ChangeEvent } from 'react';

import { cn } from '../cn';
import { composeVariant, inputVariantMutedText } from '../Input2/buttonClasses';
import Button from '../Input2/Button';
import { Files, General } from '../StrokeIcons';

export const MESSAGE_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;

export type MessageAttachmentState = {
  /** Filename of the currently attached file, or null when nothing is attached. */
  filename: string | null;
  onSelectFile: (file: File) => void;
  onRemove: () => void;
  isUploading?: boolean;
};

export type MessageAttachmentButtonPureProps = MessageAttachmentState & {
  disabled?: boolean;
  maxSizeBytes?: number;
  onError?: (message: string) => void;
  /** Presentation: 'button' (default) = compact paperclip button + filename chip;
   *  'row' = full-width clickable field styled like a FilterSelect trigger. */
  variant?: 'button' | 'row';
  /** Placeholder for the empty 'row' variant. Ignored by the 'button' variant. */
  placeholder?: string;
};

export function MessageAttachmentButtonPure({
  filename,
  onSelectFile,
  onRemove,
  isUploading = false,
  disabled = false,
  maxSizeBytes = MESSAGE_ATTACHMENT_MAX_BYTES,
  onError,
  variant = 'button',
  placeholder = 'Select a file to attach...',
}: MessageAttachmentButtonPureProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so choosing the same file again still fires `change`.
    e.target.value = '';
    if (!file) return;
    if (file.size > maxSizeBytes) {
      onError?.(`"${file.name}" is too large. Attachments must be 20MB or smaller.`);
      return;
    }
    onSelectFile(file);
  };

  // Full-width row presentation — mirrors the "append a voice clip" FilterSelect row.
  if (variant === 'row') {
    if (filename != null) {
      return (
        <div className="flex items-center gap-1">
          <Button
            size="lg"
            inputStyle
            color="blue"
            tone="outline"
            shape="square"
            bordered={false}
            readOnly
            className="flex-1 min-w-0"
            icon={<Files.PaperClip />}
            status={isUploading ? 'updating' : undefined}
            tooltip={filename}>
            <span className="truncate">{filename}</span>
          </Button>
          <Button
            size="sm"
            color="gray"
            text
            icon={<General.XMark />}
            onClick={onRemove}
            disabled={disabled || isUploading}
            tooltip="Remove attachment"
            data-testid="message-attachment-remove"
          />
        </div>
      );
    }

    return (
      <>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={handleChange}
          data-testid="message-attachment-input"
        />
        <Button
          size="lg"
          inputStyle
          color="blue"
          tone="outline"
          shape="square"
          bordered={false}
          className="w-full"
          icon={<Files.PaperClip />}
          status={isUploading ? 'updating' : undefined}
          onClick={() => inputRef.current?.click()}
          disabled={disabled || isUploading}
          tooltip="Attach a file">
          <span className={cn('truncate', inputVariantMutedText(composeVariant('blue', 'outline'), false))}>
            {placeholder}
          </span>
        </Button>
      </>
    );
  }

  if (filename != null) {
    return (
      <div className="flex flex-row items-center gap-1">
        <div className="max-w-[12rem]">
          <Button size="xs" color="blue" tone="soft" readOnly icon={<Files.PaperClip />} tooltip={filename}>
            {filename}
          </Button>
        </div>
        <Button
          size="xs"
          color="gray"
          text
          icon={<General.XMark />}
          onClick={onRemove}
          disabled={disabled || isUploading}
          tooltip="Remove attachment"
          data-testid="message-attachment-remove"
        />
      </div>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        data-testid="message-attachment-input"
      />
      <Button
        size="sm"
        color="gray"
        icon={<Files.PaperClip />}
        status={isUploading ? 'updating' : undefined}
        onClick={() => inputRef.current?.click()}
        disabled={disabled || isUploading}
        tooltip="Attach a file"
      />
    </>
  );
}
